import { ListItemButton, ListItemIcon } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import { useUpdateTranslation } from "apollo/hooks/mutations";
import { useGetTranslation, useGetTranslations } from "apollo/hooks/queries";
import { Result } from "apollo/utils";
import { useDateLocale } from "core/hooks/useDateLocale";
import { format, fromUnixTime } from "date-fns";
import RSButton, { RSFAB } from "ds_legacy/components/RSButton";
import { SpinnerPage } from "ds_legacy/components/Spinner";
import Tooltip from "ds_legacy/components/Tooltip";
import {
  APP_BACKGROUND,
  CARD_BORDER,
  ERROR_COLOR,
  SUCCESS_COLOR,
  TEXT_DARK_PRIMARY,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { border, dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  Body,
  Caption,
  Display,
  FONT_SIZE_14,
  Title,
} from "ds_legacy/materials/typography";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { sort } from "jsonabc";
import { debounce } from "lodash";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  DownloadIcon,
  HelpCircleIcon,
  PencilIcon,
  PlusCircleIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Trash2Icon,
  UploadIcon,
} from "lucide-react";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import { InputField } from "./fields";
import {
  useCanDeleteTranslation,
  useForm,
  useTranslationsEditor,
} from "./hooks";
import { locales } from "./index";
import {
  AddEntryModal,
  ExportFromSourceModal,
  HelpModal,
  RemoveEntryModal,
} from "./modals";
import {
  DispatchContext,
  TRANSLATION_SELECT,
  TRANSLATION_UPDATE,
  useTranslationsDispatch,
} from "./translationsHandler";
import {
  LocalesType,
  RepositoryType,
  Search,
  SearchType,
  Selection,
  TranslationType,
  TranslationTypes,
} from "./types";
import {
  FullWidth,
  applyTagsLogic,
  convertTranslationsToCsv,
  getEmptySelection,
  getOntology,
  isDev,
  isFilteredBySearch,
  isLocaleValue,
  isSelection,
  isTranslationValid,
  searchTags,
  translationSuggestions,
} from "./utils";

export const Container = styled.div`
  width: 100%;
  height: 100%;
  max-height: calc(100vh - ${dp(48)});
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  overflow: hidden;
`;

export const ContentContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  height: calc(100vh - 104px - 48px);
`;

export const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  max-width: 100%;
`;

export const FixedScrollingContainer = styled.div`
  max-height: 100%;
  overflow-x: scroll;
`;

export const TreeContainer = styled(FixedScrollingContainer)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
`;

export const PanelBase = styled.div`
  display: flex;
  width: 50%;
`;

export const ModificationContainer = styled(FixedScrollingContainer)`
  background-color: ${APP_BACKGROUND};
  overflow: auto;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-left: ${border({ width: 4, color: CARD_BORDER })};
`;

export const ModificationViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  align-items: center;
  padding: ${padding(0, 0, 6, 0)};
`;

export const ActionsContainer = styled.div`
  margin-bottom: 0px;
`;

export const FieldContainer = styled.div`
  width: 90%;
`;

export const ExportPositioner = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  margin: ${margin(2)};
`;

export const StatusIndicator = ({
  indicator,
}: {
  indicator: boolean | null;
}) => {
  if (indicator == null) {
    return null;
  }
  return (
    <div
      style={{
        display: "inline-flex",
        fontSize: "1.5rem",
        padding: padding(1.5),
      }}
    >
      {indicator ? (
        <Tooltip title="Your previous export was successful">
          <ThumbsUpIcon style={{ color: SUCCESS_COLOR }} size={16} />
        </Tooltip>
      ) : (
        <Tooltip title="Your previous export failed">
          <ThumbsDownIcon style={{ color: ERROR_COLOR }} size={16} />
        </Tooltip>
      )}
    </div>
  );
};

const SearchBar = ({
  indicator,
  repo,
  search,
  setSearch,
}: {
  indicator: boolean | null;
  repo: RepositoryType;
  search: Search;
  setSearch: Dispatch<SetStateAction<Search>>;
}) => {
  const [, translations] = useGetTranslations(repo);
  const [open, setOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const text = e.target.value;
    setSearch((state) => ({ ...state, text }));
  }

  const debouncedHandleChange = useMemo(() => debounce(handleChange, 350), []);

  useEffect(() => {
    return () => debouncedHandleChange.cancel();
  });

  if (!translations) return null;
  return (
    <>
      <SearchContainer>
        <TextField
          id="input_search"
          label="Search"
          placeholder={
            search.type === SearchType.Content
              ? "Search inside translations"
              : "Search translations keys"
          }
          margin="normal"
          variant="filled"
          defaultValue={search.text}
          onChange={debouncedHandleChange}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                style={{ margin: margin(0, 2, 0, 0) }}
              >
                <FormControlLabel
                  labelPlacement="start"
                  label={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        width: dp(200),
                      }}
                    >
                      {search.type === SearchType.Content
                        ? "Searching inside translations"
                        : "Searching in context"}
                    </div>
                  }
                  control={
                    <Switch
                      id="search_switcher"
                      value={search.type}
                      onChange={() =>
                        setSearch((state) => ({
                          ...state,
                          type:
                            search.type === SearchType.Content
                              ? SearchType.Context
                              : SearchType.Content,
                        }))
                      }
                    />
                  }
                />
              </InputAdornment>
            ),
          }}
          style={{ margin: margin(0), width: "100%" }}
        />
        <HorizontalLayout
          id="action_bar"
          stretched
          style={{ justifyContent: "space-between" }}
        >
          <div>
            {Object.entries(searchTags).map(
              ([key, { label, lang }]: [string, ToType]) => {
                const tag = typeof key == "string" ? parseInt(key) : key;
                return (
                  <Chip
                    id={`${lang}-${label}`}
                    key={tag}
                    color={search.tags.includes(tag) ? "primary" : undefined}
                    variant={search.tags.includes(tag) ? undefined : "outlined"}
                    avatar={<Avatar>{lang}</Avatar>}
                    label={label}
                    style={{ margin: margin(1) }}
                    clickable
                    onClick={() => {
                      if (search.tags.includes(tag))
                        setSearch((prev) => ({
                          ...prev,
                          tags: prev.tags.filter((t) => t !== tag),
                        }));
                      else
                        setSearch((prev) => ({
                          ...prev,
                          tags: prev.tags.concat(tag),
                        }));
                    }}
                  />
                );
              },
            )}
          </div>
          <div style={{ display: "flex" }}>
            <a
              href={convertTranslationsToCsv(translations)}
              download="translations.csv"
            >
              <IconButton size="large">
                <DownloadIcon size={16} />
              </IconButton>
            </a>
            {isDev && (
              <IconButton onClick={() => setExportOpen(true)} size="large">
                <UploadIcon size={16} />
              </IconButton>
            )}
            <StatusIndicator indicator={indicator} />
            <IconButton onClick={() => setOpen(true)} size="large">
              <HelpCircleIcon size={16} />
            </IconButton>
          </div>
        </HorizontalLayout>
      </SearchContainer>
      {open && <HelpModal open setOpen={setOpen} />}
      {exportOpen && (
        <ExportFromSourceModal open setOpen={setExportOpen} repo={repo} />
      )}
    </>
  );
};

function ElementCollapsed({
  config: { label, nestedLevel },
  pathEntry,
  pathKey,
  repo,
  search,
  selection,
  translations,
}: {
  config: { label: string; nestedLevel: number };
  pathEntry: AnyObject;
  pathKey: string;
  repo: RepositoryType;
  search: Search;
  selection: Selection;
  translations: AnyObject;
}) {
  const [open, setOpen] = useState(false);
  const [addModal, setAddModal] = useState(false);

  return (
    <div>
      {addModal && (
        <AddEntryModal
          open={addModal}
          setOpen={setAddModal}
          originKey={pathKey}
          translations={translations}
          repo={repo}
        />
      )}
      <ListItemButton
        divider
        id={pathKey}
        key={pathKey}
        onClick={() => setOpen(!open)}
        sx={{ pl: `calc(${dp(16)} + ${padding(nestedLevel)})` }}
      >
        <ListItemIcon>
          {open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
        </ListItemIcon>
        <ListItemText sx={{ fontSize: FONT_SIZE_14 }} primary={label} />
        <ListItemSecondaryAction>
          <IconButton onClick={() => setAddModal(true)} size="large">
            <PlusCircleIcon size={16} />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>

      <Collapse component={FullWidth} timeout="auto" unmountOnExit in={open}>
        <List component={FullWidth as ToType} disablePadding>
          {Object.entries(pathEntry).map(([nestedKey, nestedValue]) => {
            const newKey = `${pathKey}.${nestedKey}`;

            return (
              <TreeElement
                key={newKey}
                data={[newKey, nestedValue]}
                search={search}
                selection={selection}
                translations={translations}
                repo={repo}
              />
            );
          })}
        </List>
      </Collapse>
    </div>
  );
}

function Element({
  config: { label, nestedLevel },
  repo,
  selection,
  translation,
  translationKey,
}: {
  config: { label: string; nestedLevel: number };
  repo: RepositoryType;
  selection: Selection;
  translation: TranslationType;
  translationKey: string;
}) {
  const dispatch = useTranslationsDispatch();
  const [removeModal, setRemoveModal] = useState(false);

  const isSelected = isSelection(selection, translationKey);
  const canDelete = useCanDeleteTranslation({
    createdAt: translation.created_at,
  });

  return (
    <>
      {canDelete && (
        <RemoveEntryModal
          open={removeModal}
          setOpen={setRemoveModal}
          valueKey={translationKey}
          value={translation}
          repo={repo}
        />
      )}
      <ListItemButton
        divider
        id={translationKey}
        key={translationKey}
        selected={isSelected}
        dense
        onClick={() =>
          isSelected
            ? dispatch({
                type: TRANSLATION_SELECT,
                payload: { selection: getEmptySelection() },
              })
            : dispatch({
                type: TRANSLATION_SELECT,
                payload: { selection: [translationKey, translation] },
              })
        }
        sx={{ pl: `calc(${dp(16)} + ${padding(nestedLevel)})` }}
      >
        <ListItemIcon>
          <PencilIcon
            style={{ margin: margin(0, 0.5), color: TEXT_DARK_PRIMARY }}
            size={16}
          />
        </ListItemIcon>
        <ListItemText primary={label} />
        {canDelete && (
          <ListItemSecondaryAction>
            <IconButton
              data-testid={`${translationKey}-remove-button`}
              aria-label="Remove entry"
              onClick={() => setRemoveModal(true)}
              size="large"
            >
              <Trash2Icon size={16} />
            </IconButton>
          </ListItemSecondaryAction>
        )}
      </ListItemButton>
    </>
  );
}

const TreeElement = ({
  data,
  repo,
  search,
  selection,
  translations,
}: {
  data: [string, any];
  repo: RepositoryType;
  search: Search;
  selection: Selection;
  translations: AnyObject;
}) => {
  const [key, value] = data;
  const filtered = useMemo(
    () => isFilteredBySearch(search, { key, value }),
    [search, key, value],
  );

  if (filtered) return null;

  const nestedLevel = (key.match(/\./g) || []).length;
  const label = nestedLevel > 0 ? key.slice(key.lastIndexOf(".") + 1) : key;

  if (isLocaleValue(value)) {
    return (
      <Element
        translationKey={key}
        translation={value}
        selection={selection}
        config={{ nestedLevel, label }}
        repo={repo}
      />
    );
  }

  return (
    <ElementCollapsed
      pathKey={key}
      pathEntry={value}
      selection={selection}
      search={search}
      config={{ nestedLevel, label }}
      translations={translations}
      repo={repo}
    />
  );
};

const TreeView = ({
  repo,
  search,
  selection,
  translations,
}: {
  repo: RepositoryType;
  search: Search;
  selection: Selection;
  translations: AnyObject;
}) => {
  const [modal, setModal] = useState(false);

  return (
    <List component={TreeContainer as ToType}>
      <>
        {modal && (
          <AddEntryModal
            open={modal}
            setOpen={setModal}
            translations={translations}
            repo={repo}
          />
        )}
        <div>
          <ListItemButton onClick={() => setModal(true)} divider>
            <ListItemIcon>
              <PlusCircleIcon size={16} />
            </ListItemIcon>
            <ListItemText primary="Add translation in root folder" />
          </ListItemButton>
        </div>
      </>
      {Object.keys(translations).length > 0 ? (
        Object.entries(translations).map((keyValue) => (
          <TreeElement
            key={keyValue.toString()}
            data={keyValue}
            search={search}
            selection={selection}
            translations={translations}
            repo={repo}
          />
        ))
      ) : (
        <SpinnerPage id="state-population" />
      )}
    </List>
  );
};

const PlaceholderComponent = () => (
  <div
    style={{
      display: "flex",
      flex: "1 1 auto",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <Display>Select an item from the list to begin</Display>
  </div>
);

function ConnectedModificationView({
  repo,
  selection,
}: {
  repo: RepositoryType;
  selection: Selection;
}) {
  const [id] = selection;
  const [queryProgress, translation] = useGetTranslation(id, repo, {
    skip: !id,
    fetchPolicy: "network-only",
  });
  const translationOverloaded = useMemo(
    () =>
      translation != null
        ? {
            ...translation,
            tags: translation.tags || [],
            links: translation.links || [],
            description: translation.description || "",
          }
        : translation,
    [translation],
  );

  return (
    <PanelBase>
      <ModificationContainer>
        <Result
          data={translationOverloaded}
          flex
          getErrorLabel={() => "Could not load translation"}
          id="acp-translation-get"
          queryProgress={queryProgress}
          PlaceholderComponent={<PlaceholderComponent />}
        >
          {(translationOverloaded) => (
            <ModificationView translation={translationOverloaded} repo={repo} />
          )}
        </Result>
      </ModificationContainer>
    </PanelBase>
  );
}

const ModificationView = ({
  repo,
  translation,
}: {
  repo: RepositoryType;
  translation: TranslationType | undefined;
}) => {
  const dispatch = useTranslationsDispatch();
  const toast = useToast();
  const { currentLocale: locale } = useDateLocale();
  const [updateTranslation, queryProgress, resetProgress] =
    useUpdateTranslation(repo);

  const { formState, setFormValue, submit } = useForm(
    Object.keys(locales).reduce(
      (form, key) => ({
        ...form,
        [key]: {
          value: getOntology(translation, key as keyof LocalesType),
          validate: isTranslationValid(key),
        },
      }),
      Object.entries(translation || {})
        .filter(([key]) => key !== "locales")
        .reduce((init, [key, value]) => ({ ...init, [key]: { value } }), {}),
    ),
    () => {
      const { de, en, ...infos } = Object.entries(formState).reduce(
        (acc, [key, { value }]) => ({
          ...acc,
          [key]: value,
        }),
        {
          id: translation ? translation.id : "-1",
          description: "",
          en: null,
          de: null,
          links: null,
          tags: [],
        },
      );

      const newTranslation = applyTagsLogic({
        ...infos,
        locales: {
          en: en || "",
          de: de || "",
        },
      });

      updateTranslation({
        variables: {
          id: translation?.id ?? "-1",
          input: newTranslation,
        },
      }).then(() => {
        dispatch({
          type: TRANSLATION_UPDATE,
          payload: newTranslation,
        });
        toast({
          message: "Translations saved ! Don't forget to export !",
          color: "primary",
        });
        setTimeout(() => resetProgress(), 3000);
      });
    },
    [translation?.id],
  );

  useEffect(() => {
    Object.keys(locales).forEach((key) =>
      setFormValue(key, getOntology(translation, key as keyof LocalesType)),
    );
  }, [translation]);

  if (!translation?.id) return <PlaceholderComponent />;

  return (
    <ModificationViewContainer>
      <VerticalLayout>
        <HorizontalLayout aligned className="title">
          <Title>You have selected:</Title>
          <RSButton
            color="primary"
            id="id"
            loading="na"
            onClick={() => {
              if (!document.body) {
                return;
              }
              const xClip = document.createElement("input");
              xClip.setAttribute("id", "clipboard");
              xClip.setAttribute("value", `translations.${translation.id}`);
              document.body.appendChild(xClip);
              xClip.select();
              document.execCommand("copy");
              document.body.removeChild(xClip);
              toast({
                message: "Translation path copied to clipboard!",
                color: "primary",
              });
            }}
            variant="text"
          >
            <Tooltip title="Copy translation path to clipboard">
              <HorizontalLayout aligned>
                <Body style={{ textTransform: "initial" }}>
                  {translation.id}
                </Body>
                <CopyIcon style={{ color: TEXT_DARK_PRIMARY }} size={16} />
              </HorizontalLayout>
            </Tooltip>
          </RSButton>
        </HorizontalLayout>
        <HorizontalLayout aligned style={{ justifyContent: "center" }}>
          {translation.created_at && (
            <Caption>
              <b>Created on:</b>&nbsp;
              {format(
                fromUnixTime(translation.created_at),
                "dd/MM/yy - hh:mm",
                { locale },
              )}
            </Caption>
          )}
          {translation.updated_at && (
            <Caption>
              <b>Last update on:</b>&nbsp;
              {format(
                fromUnixTime(translation.updated_at),
                "dd/MM/yy - hh:mm",
                { locale },
              )}
            </Caption>
          )}
        </HorizontalLayout>
      </VerticalLayout>
      <FieldContainer>
        <InputField
          key="Description"
          label="Description"
          placeholder="Example: 'Explanation text of confirmation modal when editing search'"
          rows="1"
          value={formState.description.value}
          error={formState.description.error}
          setValue={(value) => setFormValue("description", value)}
        />
        {Object.keys(locales).map((key) => (
          <InputField
            id={key}
            tags={
              Array.isArray(formState.tags.value) ? formState.tags.value : []
            }
            setTags={(tags) => setFormValue("tags", tags)}
            suggestions={translationSuggestions}
            key={locales[key]}
            label={locales[key]}
            value={formState[key].value}
            error={formState[key].error}
            setValue={(value) => setFormValue(key, value)}
            required={key === "en"}
            onReset={
              formState[key].value !==
              getOntology(translation, key as keyof LocalesType)
                ? () =>
                    setFormValue(
                      key,
                      getOntology(translation, key as keyof LocalesType),
                    )
                : undefined
            }
          />
        ))}
      </FieldContainer>
      <ActionsContainer>
        <RSButton
          color="primary"
          id="save"
          loading={queryProgress}
          onClick={submit}
          style={{ margin: margin(1), width: dp(160) }}
          variant="contained"
        >
          Save
        </RSButton>
      </ActionsContainer>
    </ModificationViewContainer>
  );
};

function insertTop(
  object: AnyObject,
  key: string,
  entry: AnyObject,
): AnyObject {
  if (
    !Object.keys(object).length ||
    Object.values(object).every((objEntry) => isLocaleValue(objEntry))
  )
    return {
      [key]: entry,
      ...object,
    };

  const [targetKey] =
    Object.entries(object).find(([, objEntry]) => isLocaleValue(objEntry)) ||
    [];

  if (!targetKey) return { ...object, [key]: entry };

  return Object.entries(object).reduce((obj, [objKey, objEntry]) => {
    if (objKey === targetKey)
      return {
        ...obj,
        [key]: entry,
        [objKey]: objEntry,
      };
    return { ...obj, [objKey]: objEntry };
  }, {});
}

function nestOrdering(object: AnyObject): AnyObject {
  if (isLocaleValue(object)) return object;

  return Object.entries(object).reduce((obj, [key, entry]) => {
    if (!isLocaleValue(entry)) return insertTop(obj, key, nestOrdering(entry));

    return { ...obj, [key]: entry };
  }, {});
}

export const TranslationsPanel = ({
  indicator,
  onExport,
  repo,
  translationsArray,
}: {
  indicator: boolean | null;
  onExport: (translations: ToType) => void;
  repo: RepositoryType;
  translationsArray: Readonly<TranslationTypes> | undefined;
}) => {
  const [search, setSearch] = useState<Search>({
    type: SearchType.Content,
    text: "",
    tags: [],
  });
  const { dispatch, exportTranslations, selection, translations } =
    useTranslationsEditor({
      translationsArray,
      onExport,
    });

  const orderedTranslations = useMemo(() => {
    const alphaOrdered = JSON.parse(
      sort(JSON.stringify(translations.toJS(), null, 2), true),
    );

    return nestOrdering(alphaOrdered);
  }, [translations]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <Container>
        <SearchBar
          indicator={indicator}
          search={search}
          setSearch={setSearch}
          repo={repo}
        />

        <ContentContainer>
          <TreeView
            translations={orderedTranslations}
            search={search}
            selection={selection}
            repo={repo}
          />
          <ConnectedModificationView selection={selection} repo={repo} />
        </ContentContainer>
        <ExportPositioner>
          <RSFAB
            onClick={exportTranslations}
            color="secondary"
            variant="extended"
          >
            <b style={{ width: dp(150) }}>Export</b>
          </RSFAB>
        </ExportPositioner>
      </Container>
    </DispatchContext.Provider>
  );
};
